import { Fragment, useRef } from "react";
import { REGION_ORDER, REGION_STYLE, displayCell } from "@/lib/report";
import { thumbUrl } from "@/components/DynamicForm";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, Trash2 } from "lucide-react";

const ALIGN_CLASS = { r: "text-right", c: "text-center", l: "text-left" };
const alignClass = (align) => ALIGN_CLASS[align] || "text-left";

function ResizableThumb({ item, onResize }) {
  const ref = useRef(null);
  const startRef = useRef(null);
  const resized = !!(item.w && item.h);
  const w = resized ? item.w : Math.round(42 * (item.scale || 1));
  const h = resized ? item.h : Math.round(72 * (item.scale || 1));
  const onDown = () => {
    const el = ref.current;
    if (el) startRef.current = [el.clientWidth, el.clientHeight];
  };
  const commit = () => {
    const el = ref.current;
    if (!el || !startRef.current) return;
    const [sw, sh] = startRef.current;
    startRef.current = null;
    if (Math.abs(el.clientWidth - sw) < 2 && Math.abs(el.clientHeight - sh) < 2) return; // click, not a resize
    onResize({ w: Math.round(el.clientWidth), h: Math.round(el.clientHeight) });
  };
  return (
    <div ref={ref} data-testid="resizable-thumb" title="Drag the bottom-right corner to resize"
      onMouseDown={onDown} onMouseUp={commit} onTouchStart={onDown} onTouchEnd={commit}
      style={{ width: w, height: h, resize: "both", overflow: "hidden", boxSizing: "content-box",
        minWidth: 24, minHeight: 24, maxWidth: 280, maxHeight: 340 }}
      className="rounded border border-slate-300 bg-slate-50">
      <img src={thumbUrl(item.path)} alt="" draggable={false}
        style={{ width: "100%", height: "100%", objectFit: resized ? "fill" : "cover" }} />
    </div>
  );
}

export default function DynamicTable({ report, records, onEdit, onDelete, onResizeImage, editingId,
  selectionEnabled = true, readOnly = false, selectedIds = [], onSelectionChange, highlightedId }) {
  const cols = report.columns || [];
  const statusColors = report.status_colors || {};
  const sumFields = report.sum_fields || [];
  const totalCols = cols.length + 1 + (selectionEnabled ? 1 : 0) + (readOnly ? 0 : 1);
  const groupBy = report.group_rows_by;
  const mergeCols = new Set(report.merge_cols || []);
  const countCol = report.count_col;
  const allSelected = selectionEnabled && records.length > 0 && records.every((record) => selectedIds.includes(record.id));
  const someSelected = selectionEnabled && records.some((record) => selectedIds.includes(record.id));

  const toggleAll = (checked) => {
    const visible = new Set(records.map((record) => record.id));
    const next = checked
      ? Array.from(new Set([...selectedIds, ...visible]))
      : selectedIds.filter((id) => !visible.has(id));
    onSelectionChange?.(next);
  };

  const toggleOne = (id, checked) => {
    const next = checked
      ? Array.from(new Set([...selectedIds, id]))
      : selectedIds.filter((selectedId) => selectedId !== id);
    onSelectionChange?.(next);
  };

  const renderCell = (c, r) => {
    if (c.type === "image") {
      const items = (r[c.key] || []).map((it) => (typeof it === "string" ? { path: it } : it)).filter((it) => it && it.path);
      if (!items.length) return <span className="text-slate-300">—</span>;
      return (
        <div className="flex flex-wrap gap-1.5 items-start">
          {items.map((it, i) => (
            <ResizableThumb key={it.path} item={it}
              onResize={(dims) => onResizeImage && onResizeImage(r, c.key, i, dims)} />
          ))}
        </div>
      );
    }
    const text = displayCell(c, r, report);
    if (c.colored) {
      const color = statusColors[r[c.key]] || "#111827";
      return <span className="font-semibold" style={{ color }}>{text}</span>;
    }
    if (c.is_date) {
      const dateSpan = r.date_red ? <span className="text-red-600 font-semibold">{text}</span> : <span>{text}</span>;
      if (!r.carried_from_month) return dateSpan;
      return (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          {dateSpan}
          <span data-testid={`carried-badge-${r.id}`}
            title={`Carried forward from ${r.carried_from_month} — still unresolved`}
            className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-amber-100 text-amber-700 whitespace-nowrap">
            ↻ {r.carried_from_month}
          </span>
        </span>
      );
    }
    if (c.type === "amount") return <span className="font-mono-num font-semibold">{text}</span>;
    if (c.type === "number") return <span className="font-mono-num">{text}</span>;
    return text;
  };

  const tdClass = (c) =>
    `py-2 px-2 ${alignClass(c.align)} ` +
    `${["textarea", "multiselect"].includes(c.type) ? "min-w-[150px] whitespace-pre-line" : ""} ` +
    `${["outlet", "name"].includes(c.key) ? "font-medium text-slate-800" : "text-slate-600"} text-sm`;

  const actionsCell = (r) => (
    <td className="py-2 px-2 whitespace-nowrap align-middle">
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(r)} data-testid={`edit-btn-${r.id}`}>
          <Pencil className="w-3.5 h-3.5 text-slate-500" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 hover:bg-red-50" onClick={() => onDelete(r)} data-testid={`delete-btn-${r.id}`}>
          <Trash2 className="w-3.5 h-3.5 text-red-500" />
        </Button>
      </div>
    </td>
  );

  const dataRow = (r, idx, mctx) => {
    const active = r.id === editingId;
    const highlighted = r.id === highlightedId;
    const bg = r.row_highlight ? "#FFF59D" : (r.region ? REGION_STYLE[r.region]?.tint : "#fff");
    return (
      <tr key={r.id} data-testid={`record-row-${r.id}`} data-search-record-id={r.id}
        className={`border-b border-slate-100 hover:brightness-95 transition-[filter,box-shadow,outline-color] duration-300 ${active ? "ring-2 ring-inset ring-slate-900" : ""} ${highlighted ? "relative z-10 ring-4 ring-inset ring-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]" : ""} ${selectedIds.includes(r.id) ? "outline outline-1 outline-sky-400 -outline-offset-1" : ""}`}
        style={{ backgroundColor: bg }}>
        {selectionEnabled && (
          <td className="w-9 px-2 py-2 text-center align-middle">
            <Checkbox checked={selectedIds.includes(r.id)} onCheckedChange={(checked) => toggleOne(r.id, checked === true)}
              aria-label={`Select record ${idx}`} data-testid={`select-record-${r.id}`} />
          </td>
        )}
        <td className="py-2 px-2 text-slate-400 font-mono-num text-xs align-middle">{idx}</td>
        {cols.map((c) => {
          if (mctx && mergeCols.has(c.key)) {
            if (mctx.idx !== 0) return null; // covered by rowSpan above
            return (
              <td key={c.key} rowSpan={mctx.groupLen} className={`${tdClass(c)} align-middle`}>
                {c.key === countCol ? <span className="font-mono-num font-bold">{mctx.groupLen}</span> : renderCell(c, r)}
              </td>
            );
          }
          return <td key={c.key} className={tdClass(c)}>{renderCell(c, r)}</td>;
        })}
        {!readOnly && actionsCell(r)}
      </tr>
    );
  };

  if (records.length === 0) {
    return (
      <div className="py-24 text-center text-slate-400" data-testid="empty-state">
        <p className="text-lg font-semibold">No records yet</p>
        <p className="text-sm">Add your first entry using the form above.</p>
      </div>
    );
  }

  const groups = report.region_group
    ? REGION_ORDER.map((rg) => ({ region: rg, items: records.filter((r) => r.region === rg) })).filter((g) => g.items.length)
    : [{ region: null, items: records }];

  let counter = 0;

  const renderItems = (items) => {
    if (!groupBy) return items.map((r) => { counter += 1; return dataRow(r, counter, null); });
    const order = [], buckets = {};
    items.forEach((r) => {
      const k = String(r[groupBy] ?? "");
      if (!buckets[k]) { buckets[k] = []; order.push(k); }
      buckets[k].push(r);
    });
    const out = [];
    order.forEach((k) => {
      const sub = buckets[k];
      sub.forEach((r, idx) => { counter += 1; out.push(dataRow(r, counter, { idx, groupLen: sub.length })); });
    });
    return out;
  };

  return (
    <div className="max-h-[70vh] overflow-auto 2xl:max-h-none 2xl:overflow-visible" data-testid="records-table">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-20 bg-white shadow-[0_3px_8px_rgba(15,23,42,0.10)] 2xl:top-[64px]" data-testid="records-table-header">
          <tr className="border-b-2 border-slate-200 bg-white">
            {selectionEnabled && (
              <th className="w-9 px-2 py-2.5 text-center">
                <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                  onCheckedChange={(checked) => toggleAll(checked === true)} aria-label="Select all visible records"
                  data-testid="select-all-records" />
              </th>
            )}
            <th className="py-2.5 px-2 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">#</th>
            {cols.map((c) => (
              <th key={c.key} className={`py-2.5 px-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 whitespace-nowrap ${c.type === "image" ? "text-center" : alignClass(c.align)}`}>{c.label}</th>
            ))}
            {!readOnly && <th className="py-2.5 px-2"></th>}
          </tr>
        </thead>
        <tbody>
          {groups.map(({ region, items }) => (
            <Fragment key={region || "all"}>
              {region && (
                <tr>
                  <td colSpan={totalCols} className="py-1.5 px-3 font-bold text-sm text-white text-center"
                    style={{ backgroundColor: REGION_STYLE[region].banner }} data-testid={`region-banner-${region}`}>
                    {region}
                  </td>
                </tr>
              )}
              {renderItems(items)}
              {report.subtotal_per_region && sumFields.length > 0 && (() => {
                const firstSum = cols.findIndex((c) => sumFields.includes(c.key));
                return (
                  <tr className="border-t-2 border-slate-300 bg-white">
                    <td colSpan={firstSum + (selectionEnabled ? 2 : 1)} className="py-2 px-2 text-right font-bold text-slate-700">Subtotal</td>
                    {cols.slice(firstSum).map((c) => {
                      if (!sumFields.includes(c.key)) return <td key={c.key}></td>;
                      const val = items.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
                      return <td key={c.key} className={`py-2 px-2 font-bold text-slate-900 font-mono-num ${c.align === "r" ? "text-right" : "text-center"}`}
                        data-testid={`subtotal-${region}-${c.key}`}>{c.type === "amount" ? `RM${val.toLocaleString("en-MY", { minimumFractionDigits: 2 })}` : val}</td>;
                    })}
                    {!readOnly && <td></td>}
                  </tr>
                );
              })()}
            </Fragment>
          ))}
          {report.grand_total && sumFields.length > 0 && (() => {
            const firstSum = cols.findIndex((c) => sumFields.includes(c.key) || c.key === report.row_total_field);
            return (
              <tr className="border-t-2 border-slate-900 bg-slate-50">
                <td colSpan={firstSum + (selectionEnabled ? 2 : 1)} className="py-2.5 px-2 text-right font-extrabold text-blue-700">Total</td>
                {cols.slice(firstSum).map((c) => {
                  const isSum = sumFields.includes(c.key);
                  const isTol = c.key === report.row_total_field;
                  if (!isSum && !isTol) return <td key={c.key}></td>;
                  const val = isTol
                    ? records.reduce((s, r) => s + sumFields.reduce((a, f) => a + (Number(r[f]) || 0), 0), 0)
                    : records.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
                  const isAmt = c.type === "amount";
                  return <td key={c.key} data-testid={`grand-total-${c.key}`}
                    className={`py-2.5 px-2 font-extrabold text-blue-700 font-mono-num ${c.align === "r" ? "text-right" : "text-center"} ${isAmt ? "border-b-4 border-double border-red-600" : ""}`}>
                    {isAmt ? `RM${val.toLocaleString("en-MY", { minimumFractionDigits: 2 })}` : val}</td>;
                })}
                {!readOnly && <td></td>}
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
}
